import find from 'lodash/find';
import {
  applySnapshot,
  cast,
  clone,
  flow,
  Instance,
  types,
} from 'mobx-state-tree';
import { killNulls } from 'utils';
import { IObject, IPhone } from 'types';
import { Providers } from 'http-requests';
import { ContractorDTO } from 'http-requests/providers/dto';
import { IParams } from 'http-requests/types';
import { ISelectValue } from 'components/ui';

import { RequestStateModel } from './general-models';

export interface IProvider extends Instance<typeof ProviderModel> {}
export interface IBusinessTypeProviderModel extends Instance<
  typeof BusinessTypeProviderModel
> {}

const ProvidersStatusesModel = types.model({
  providersList: types.optional(RequestStateModel, {}),
  providersListByIds: types.optional(RequestStateModel, {}),
  providerContacts: types.optional(RequestStateModel, {}),
  businessTypeProviders: types.optional(RequestStateModel, {}),
  baseProvidersList: types.optional(RequestStateModel, {}),
  contractors: types.optional(RequestStateModel, {}),
  workerProviders: types.optional(RequestStateModel, {}),
});

const PointModel = types.model({
  coordinates: types.optional(types.array(types.number), []),
  type: types.optional(types.string, ''),
});

const AddressInfoModel = types.model({
  house_number: types.optional(types.string, ''),
  fias_addrobjs: types.optional(types.array(types.string), []),
  fias_street_guid: types.optional(types.string, ''),
  postal_code: types.optional(types.string, ''),
  fias_house_guid: types.optional(types.maybeNull(types.string), null),
  area_number: types.optional(types.maybeNull(types.string), ''),
  location: types.optional(types.string, ''),
  point: types.optional(types.maybeNull(PointModel), null),
});

const AddressModel = types.model({
  postal: types.optional(AddressInfoModel, {}),
  correspondence: types.optional(AddressInfoModel, {}),
  real: types.optional(AddressInfoModel, {}),
});

const ProviderModel = types.model('ProviderModel', {
  _id: types.optional(types.string, ''),
  str_name: types.optional(types.string, ''),
  business_types: types.optional(types.array(types.string), []),
  address: types.optional(AddressModel, {}),
  ogrn: types.optional(types.string, ''),
  inn: types.optional(types.string, ''),
  sbis: types.optional(types.boolean, false),
  managers: types.optional(types.array(types.string), []),
  ticket_rate: types.optional(types.string, ''),
  crm_status: types.optional(types.string, ''),
});

const PhoneModel = types.model({
  id: types.optional(types.maybeNull(types.string), null),
  add: types.optional(types.maybeNull(types.string), null),
  code: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  str_number: types.optional(types.string, ''),
  phone_type: types.optional(types.string, ''),
  added_by_tenant: types.optional(types.boolean, false),
  not_actual: types.optional(types.boolean, false),
});

const ContactsModel = types.model({
  id: types.optional(types.string, ''),
  dispatcher: types.optional(types.maybeNull(PhoneModel), null),
  emergency: types.optional(types.maybeNull(PhoneModel), null),
});

const BusinessTypeProviderModel = types.model({
  id: types.optional(types.string, ''),
  str_name: types.optional(types.string, ''),
  url: types.optional(types.maybeNull(types.string), null),
  real_address: types.optional(types.maybeNull(types.string), null),
});

const AbbreviatedProviderModel = types.model({
  inn: types.string,
  ogrn: types.string,
  real_address: types.maybeNull(types.string),
  str_name: types.string,
});

const BaseProviderModel = types.model('BaseProviderModel', {
  id: types.string,
  sbis: types.boolean,
  status: types.string,
  ticket_rate: types.string,
  // TODO на самом деле данных там гораздо больше, но это всё нужное в данный момент
  provider: types.model('provider', {
    id: types.string,
    legal_form: types.string,
    name: types.string,
    str_name: types.string,
    ogrn: types.string,
    inn: types.string,
  }),
});

export const ProvidersStore = types
  .model('ProvidersStore', {
    providersList: types.optional(types.array(ProviderModel), []),
    providerContacts: types.optional(ContactsModel, {}),
    statuses: types.optional(ProvidersStatusesModel, {}),
    businessTypeProviders: types.optional(
      types.array(BusinessTypeProviderModel),
      []
    ),
    selectedProviders: types.optional(types.array(ProviderModel), []),
    baseProvidersList: types.optional(types.array(BaseProviderModel), []),
    workerProviders: types.optional(types.map(AbbreviatedProviderModel), {}),
  })
  .views((self) => ({
    get providersListOptions() {
      //TODO: сейчас нигде не используется, но может пригодиться
      return self.providersList.map((provider) => ({
        label: provider.str_name,
        value: provider._id,
      }));
    },
    get emergencyPhones(): IPhone[] {
      return !!self.providerContacts.emergency
        ? [self.providerContacts.emergency]
        : [];
    },
    get dispatcherPhones(): IPhone[] {
      return !!self.providerContacts.dispatcher
        ? [self.providerContacts.dispatcher]
        : [];
    },
    get businessTypeProvidersOptions(): ISelectValue[] {
      return self.businessTypeProviders.map(
        (business_type: IBusinessTypeProviderModel) => ({
          label: business_type.str_name,
          value: business_type.id,
        })
      );
    },
    getProviderById(providerId: string): IProvider | undefined {
      return find(self.providersList, { _id: providerId });
    },
  }))
  .actions((self) => ({
    setSelectedProviders(value: IProvider[] | IProvider) {
      if (Array.isArray(value)) {
        self.selectedProviders = cast(clone(value));
      } else {
        self.selectedProviders.push(clone(value));
      }
    },
    removeSelectedProvider(provider: IProvider) {
      self.selectedProviders.remove(provider);
    },
    clearSelectedProviders() {
      self.selectedProviders = cast([]);
    },
    clearProvidersList() {
      applySnapshot(self.providersList, []);
    },
    reset() {
      applySnapshot(self, {});
    },
    getProvidersList: flow(function* (params: IParams) {
      try {
        self.statuses.providersList.loadingStates();
        const response = yield Providers.GetProviders(params);

        self.providersList = response.data;
        self.statuses.providersList.successStates();

        return self.providersList;
      } catch (e) {
        self.statuses.providersList.errorStates();
        console.error(e);
      } finally {
        self.statuses.providersList.finallyStates();
      }
    }),
    getProvidersListByIds: flow(function* (
      providers: string[],
      signal?: AbortSignal
    ) {
      try {
        self.statuses.providersListByIds.loadingStates();
        const response = yield Providers.GetProvidersByIds(
          { id__in: providers },
          signal
        );

        response.forEach((item: any) => {
          self.selectedProviders.push(
            ProviderModel.create({
              _id: item.id,
              str_name: item.str_name,
            })
          );
        });
        self.statuses.providersListByIds.successStates();

        return response;
      } catch (error) {
        if ((error as DOMException).name === 'AbortError') {
          return;
        }

        console.error(error);
        self.statuses.providersListByIds.errorStates();
      } finally {
        self.statuses.providersListByIds.finallyStates();
      }
    }),
    getProviderContacts: flow(function* () {
      try {
        self.statuses.providerContacts.fulfilled = false;
        self.statuses.providerContacts.error = false;
        self.statuses.providerContacts.pending = true;

        self.providerContacts = yield Providers.GetProviderPhones();

        self.statuses.providerContacts.fulfilled = true;

        return self.providerContacts;
      } catch (e) {
        self.statuses.providerContacts.error = true;
        console.error(e);
      } finally {
        self.statuses.providerContacts.pending = false;
      }
    }),
    getBusinessTypeProviders: flow(function* () {
      try {
        self.statuses.businessTypeProviders.loadingStates();

        const response: IObject = yield Providers.GetBusinessTypeProviders();

        self.businessTypeProviders = response.boiler_providers;
        self.statuses.businessTypeProviders.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.statuses.businessTypeProviders.errorStates();
      } finally {
        self.statuses.businessTypeProviders.finallyStates();
      }
    }),
    addProviderPhone: flow(function* (data: { [key: string]: any }) {
      try {
        self.statuses.providerContacts.fulfilled = false;
        self.statuses.providerContacts.error = false;
        self.statuses.providerContacts.pending = true;

        self.providerContacts = yield Providers.CreateProviderPhone(data);

        self.statuses.providerContacts.fulfilled = true;

        return self.providerContacts;
      } catch (e) {
        self.statuses.providerContacts.error = true;
        console.error(e);
      } finally {
        self.statuses.providerContacts.pending = false;
      }
    }),
    updateProviderPhone: flow(function* (data: { [key: string]: any }) {
      try {
        self.statuses.providerContacts.fulfilled = false;
        self.statuses.providerContacts.error = false;
        self.statuses.providerContacts.pending = true;

        self.providerContacts = yield Providers.UpdateProviderPhone(data);

        self.statuses.providerContacts.fulfilled = true;

        return self.providerContacts;
      } catch (e) {
        self.statuses.providerContacts.error = true;
        console.error(e);
      } finally {
        self.statuses.providerContacts.pending = false;
      }
    }),
    deleteProviderPhone: flow(function* (data: { [key: string]: any }) {
      try {
        self.statuses.providerContacts.fulfilled = false;
        self.statuses.providerContacts.error = false;
        self.statuses.providerContacts.pending = true;

        self.providerContacts = yield Providers.DeleteProviderPhone(data);

        self.statuses.providerContacts.fulfilled = true;

        return self.providerContacts;
      } catch (e) {
        self.statuses.providerContacts.error = true;
        console.error(e);
      } finally {
        self.statuses.providerContacts.pending = false;
      }
    }),
    getBaseProvidersList: flow(function* (params?: IObject) {
      try {
        params = killNulls({ limit: 1500, ...params });

        self.statuses.baseProvidersList.loadingStates();
        const response: IObject = yield Providers.GetProvidersList(params);
        self.baseProvidersList = response.results;
        self.statuses.baseProvidersList.successStates();

        return response;
      } catch (e) {
        self.statuses.baseProvidersList.errorStates();
        console.error(e);
      } finally {
        self.statuses.baseProvidersList.finallyStates();
      }
    }),
    getContractors: flow(function* (
      params?: Parameters<typeof Providers.GetContractors>[0]
    ) {
      try {
        self.statuses.contractors.loadingStates();

        const response: ContractorDTO[] =
          yield Providers.GetContractors(params);
        self.statuses.contractors.successStates();

        return response;
      } catch (error) {
        self.statuses.contractors.errorStates();
        console.error(error);
      } finally {
        self.statuses.contractors.finallyStates();
      }
    }),
    getAccountsProviders: flow(function* (params: IParams) {
      try {
        self.statuses.workerProviders.loadingStates();
        self.workerProviders = yield Providers.GetProvidersAbbreviated(params);
        self.statuses.workerProviders.successStates();
      } catch (error) {
        self.statuses.workerProviders.errorStates();
        console.error(error);
      } finally {
        self.statuses.workerProviders.finallyStates();
      }
    }),
  }));
